import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tournament, Team, getMatchRules } from '@/types';
import { getTournament, getMatchesByTournament, getTeams, getTournamentStats, discardMatch, getParticipatingTeams } from '@/lib/supabase';
import { saveMatchRef } from '@/lib/persistence';
import { useAuth } from '@/lib/auth';
import AppHeader from '@/components/AppHeader';
import TournamentStatsModal from '@/components/modals/TournamentStatsModal';
import TeamChip from '@/components/TeamChip';
import ScheduleMatchModal from '@/components/modals/ScheduleMatchModal';
import StartMatchTossModal from '@/components/modals/StartMatchTossModal';

interface MatchWithTeams {
  id: string;
  matchNumber: number;
  broadcastId: string;
  date: string;
  matchTime: string | null;
  matchType: 'league' | 'playoff';
  status: string;
  winnerId: string | null;
  teamA: Team | null;
  teamB: Team | null;
  winningPoint: number | null;
  goldenPoint: number | null;
  teamAId: string;
  teamBId: string;
  startTime: string | null;
  endTime: string | null;
  liveStreamUrl: string | null;
  sets: Array<{ id: string; setNumber: number; scoreA: number; scoreB: number; winnerId: string | null; startedAt: string; endedAt: string | null }>;
}

type ActiveTab = 'matches' | 'summary';

interface TournamentDashboardProps {
  onSignOut: () => void;
}

function formatDuration(startTime: string | null, endTime: string | null): string {
  if (!startTime) return '—';
  const start = new Date(startTime).getTime();
  const end = endTime ? new Date(endTime).getTime() : Date.now();
  const totalSecs = Math.floor((end - start) / 1000);
  if (totalSecs < 0) return '—';
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${mins}m ${String(secs).padStart(2, '0')}s`;
}

function formatTime(time: string | null): string {
  if (!time) return '—';
  const [h, m] = time.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${m} ${ampm}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function getMatchScore(match: MatchWithTeams): string[] {
  const status = match.status;
  const completedSets = match.sets.filter(s => s.winnerId !== null);

  if (status === 'scheduled' || status === 'setup' || status === 'first-server') {
    return ['Not Started'];
  }
  if ((status === 'live' || status === 'set-won') && completedSets.length === 0) {
    return ['In Progress'];
  }
  const sets = completedSets.sort((a, b) => a.setNumber - b.setNumber);
  if (status === 'live' || status === 'set-won' || status === 'match-won') {
    return sets.length > 0 ? sets.map(s => `${s.scoreA}–${s.scoreB}`) : ['—'];
  }
  return ['—'];
}

function getStatusLabel(status: string): { label: string; bgClass: string; textClass: string } {
  switch (status) {
    case 'scheduled':
    case 'setup':
    case 'first-server':
      return { label: 'SCHEDULED', bgClass: 'bg-surface-raised', textClass: 'text-text-muted' };
    case 'match-won':
      return { label: 'COMPLETED', bgClass: 'bg-success/20', textClass: 'text-success' };
    case 'live':
    case 'set-won':
      return { label: 'LIVE', bgClass: 'bg-warning/20', textClass: 'text-warning' };
    default:
      return { label: 'PENDING', bgClass: 'bg-surface-raised', textClass: 'text-text-muted' };
  }
}

export default function TournamentDashboard({ onSignOut }: TournamentDashboardProps) {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const { isAdmin, isViewer, isScorer } = useAuth();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [matches, setMatches] = useState<MatchWithTeams[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [participatingTeams, setParticipatingTeams] = useState<Team[]>([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, live: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState<MatchWithTeams | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const [showPointsTable, setShowPointsTable] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('matches');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [startMatchTarget, setStartMatchTarget] = useState<MatchWithTeams | null>(null);
  const [editMatchTarget, setEditMatchTarget] = useState<MatchWithTeams | null>(null);
  const [, setNow] = useState(Date.now());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyBroadcastUrl = async (broadcastId: string) => {
    try {
      const baseUrl = import.meta.env.VITE_PUBLIC_BASE_URL || window.location.origin;
      const url = `${baseUrl}/overlay/${broadcastId}`;
      await navigator.clipboard.writeText(url);
      setCopiedId(broadcastId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Tick live durations every 30s
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const formatMatchesData = useCallback((matchesData: any[], formattedTeams: Team[]): MatchWithTeams[] => {
    return matchesData.map((m: any) => ({
      id: m.id,
      matchNumber: m.match_number,
      broadcastId: m.broadcast_id,
      date: m.date,
      matchTime: m.match_time,
      matchType: m.match_type,
      status: m.status,
      winnerId: m.winner_id,
      teamA: formattedTeams.find((t) => t.id === m.team_a_id) || null,
      teamB: formattedTeams.find((t) => t.id === m.team_b_id) || null,
      winningPoint: m.winning_point,
      goldenPoint: m.golden_point,
      teamAId: m.team_a_id,
      teamBId: m.team_b_id,
      startTime: m.start_time,
      endTime: m.end_time,
      liveStreamUrl: m.live_stream_url ?? null,
      sets: (m.sets || []).map((s: any) => ({
        id: s.id,
        setNumber: s.set_number,
        scoreA: s.score_a,
        scoreB: s.score_b,
        winnerId: s.winner_id,
        startedAt: s.started_at,
        endedAt: s.ended_at,
      })),
    }));
  }, []);

  useEffect(() => {
    if (!tournamentId) return;
    const loadData = async () => {
      setLoading(true);
      try {
        const [tournamentData, matchesData, teamsData, statsData, participatingTeamsData] = await Promise.all([
          getTournament(tournamentId),
          getMatchesByTournament(tournamentId),
          getTeams(),
          getTournamentStats(tournamentId),
          getParticipatingTeams(tournamentId),
        ]);

        setTournament(tournamentData);
        setStats(statsData);

        const formattedTeams: Team[] = teamsData.map((t: any) => ({
          id: t.id,
          name: t.name,
          shortName: t.short_name,
          color: t.color,
          logoUrl: t.logo_url,
          players: (t.players || []).map((p: any) => ({
            id: p.id, name: p.name, number: p.number, teamId: p.team_id,
          })),
        }));

        setTeams(formattedTeams);
        setParticipatingTeams(participatingTeamsData);
        setMatches(formatMatchesData(matchesData, formattedTeams));
      } catch (err) {
        console.error('Failed to load tournament:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [tournamentId, formatMatchesData]);

  const refreshData = useCallback(async () => {
    if (!tournamentId) return;
    try {
      const [matchesData, statsData] = await Promise.all([
        getMatchesByTournament(tournamentId),
        getTournamentStats(tournamentId),
      ]);
      setStats(statsData);
      setMatches(formatMatchesData(matchesData, teams));
    } catch (err) {
      console.error('Failed to refresh:', err);
    }
  }, [tournamentId, teams, formatMatchesData]);

  const leagueMatches = useMemo(() => matches.filter(m => m.matchType === 'league'), [matches]);
  const playoffMatches = useMemo(() => matches.filter(m => m.matchType === 'playoff'), [matches]);

  const isMatchLive = (status: string) => status === 'live' || status === 'set-won';
  const isMatchCompleted = (status: string) => status === 'match-won';
  const handleMatchClick = (match: MatchWithTeams) => {
    if (isMatchLive(match.status) || isMatchCompleted(match.status)) {
      navigate(`/match/${match.id}/resume`);
      return;
    }
    if (isViewer) return;
    if (match.status === 'scheduled') {
      setStartMatchTarget(match);
      return;
    }
    // first-server or setup legacy
    saveMatchRef({
      matchId: match.id,
      teamAId: match.teamAId,
      teamBId: match.teamBId,
      status: match.status,
      tournamentId,
    });
    navigate(`/match/${match.id}/resume`);
  };

  const handleDiscardMatch = async () => {
    if (!confirmDiscard) return;
    setDiscarding(true);
    try {
      await discardMatch(confirmDiscard.id);
      setActiveMenu(null);
      setConfirmDiscard(null);
      await refreshData();
    } catch (err) {
      console.error('Failed to discard match:', err);
    } finally {
      setDiscarding(false);
    }
  };

  const handleMatchStarted = (matchData: any) => {
    setStartMatchTarget(null);
    saveMatchRef({
      matchId: matchData.id,
      teamAId: matchData.team_a_id,
      teamBId: matchData.team_b_id,
      status: 'first-server',
      tournamentId,
    });
    navigate(`/match/${matchData.id}/resume`);
  };

  const getMatchRulesDisplay = (match: MatchWithTeams) => {
    const rules = tournament ? {
      leagueWinningPoint: tournament.leagueWinningPoint,
      leagueGoldenPoint: tournament.leagueGoldenPoint,
      playoffWinningPoint: tournament.playoffWinningPoint,
      playoffGoldenPoint: tournament.playoffGoldenPoint,
    } : null;
    const rulesConfig = getMatchRules(match.matchType, rules, match.winningPoint, match.goldenPoint);
    return `${rulesConfig.winningPoint}/${rulesConfig.goldenPoint}`;
  };

  const renderTeamInline = (team: Team | null) => {
    if (!team) return null;
    return (
      <div className="flex items-center gap-1.5">
        {team.logoUrl ? (
          <img src={team.logoUrl} alt={team.shortName} loading="lazy" className="w-4 h-4 rounded object-contain" onError={e => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: team.color }} />
        )}
        <span className="text-sm font-semibold" style={{ color: team.color }}>{team.shortName}</span>
      </div>
    );
  };

  const renderMatchCard = (match: MatchWithTeams) => {
    const statusInfo = getStatusLabel(match.status);
    const canModify = isAdmin || isScorer;
    const showMenu = canModify && !isMatchCompleted(match.status);
    const canStart = (isAdmin || isScorer) && match.status === 'scheduled';

    return (
      <div key={match.id} className="relative">
        <button
          onClick={() => handleMatchClick(match)}
          className="card w-full text-left hover:border-text-muted transition-colors cursor-pointer"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-text-muted">Match #{match.matchNumber}</span>
              <button
                onClick={(e) => { e.stopPropagation(); copyBroadcastUrl(match.broadcastId); }}
                className="flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-info/10 text-info hover:bg-info/20 transition-colors"
                title={`Click to copy overlay URL`}
              >
                <span>{match.broadcastId}</span>
                {copiedId === match.broadcastId ? (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                )}
              </button>
            </div>
            <span className={`px-2 py-1 text-xs font-semibold rounded ${statusInfo.bgClass} ${statusInfo.textClass}`}>
              {statusInfo.label}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {renderTeamInline(match.teamA)}
              <span className="text-text-muted text-xs font-medium">vs</span>
              {renderTeamInline(match.teamB)}
            </div>
            <span className="text-xs text-text-muted">{getMatchRulesDisplay(match)}</span>
          </div>
          {isMatchLive(match.status) && (
            <div className="mt-2 pt-2 border-t border-border flex items-center justify-between">
              <span className="text-xs text-text-muted">{isViewer ? 'Click to view' : 'Click to resume'}</span>
              <span className="text-xs text-warning font-semibold animate-pulse">In Progress</span>
            </div>
          )}
          {isMatchCompleted(match.status) && match.winnerId && (
            <div className="mt-2 pt-2 border-t border-border flex items-center justify-between">
              <span className="text-xs text-success">
                Winner: {match.winnerId === match.teamA?.id ? match.teamA?.name : match.teamB?.name}
              </span>
              {match.startTime && (
                <span className="text-xs text-text-muted">{formatDuration(match.startTime, match.endTime)}</span>
              )}
            </div>
          )}
          {match.status === 'scheduled' && match.matchTime && (
            <div className="mt-2 pt-2 border-t border-border flex items-center justify-between">
              <span className="text-xs text-text-muted">{formatDate(match.date)} {formatTime(match.matchTime)}</span>
              {canStart && <span className="text-xs text-serving font-semibold">Tap to start</span>}
            </div>
          )}
        </button>

        {showMenu && (
          <button
            onClick={e => { e.stopPropagation(); setActiveMenu(activeMenu === match.id ? null : match.id); }}
            className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-surface-raised transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-text-muted">
              <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
            </svg>
          </button>
        )}

        {activeMenu === match.id && (
          <div className="absolute top-10 right-2 z-10 bg-surface border border-border rounded-lg shadow-lg overflow-hidden min-w-[160px]">
            {isMatchLive(match.status) && (
              <>
                <button onClick={() => { setActiveMenu(null); navigate(`/match/${match.id}/resume`); }} className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-raised transition-colors flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5,3 19,12 5,21" /></svg>
                  Resume Match
                </button>
                <button onClick={() => { setActiveMenu(null); navigate(`/match/${match.id}/resume`); }} className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-raised transition-colors flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                  View Details
                </button>
              </>
            )}
            {match.status === 'scheduled' && canStart && (
              <button onClick={() => { setActiveMenu(null); setStartMatchTarget(match); }} className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-raised transition-colors flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5,3 19,12 5,21" /></svg>
                Start Match
              </button>
            )}
            {(match.status === 'first-server' || match.status === 'setup') && canModify && (
              <button onClick={() => { setActiveMenu(null); handleMatchClick(match); }} className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-raised transition-colors flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5,3 19,12 5,21" /></svg>
                Resume Setup
              </button>
            )}
            {isAdmin && !isMatchCompleted(match.status) && (
              <button
                onClick={() => {
                  setActiveMenu(null);
                  setEditMatchTarget(match);
                }}
                className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-raised transition-colors flex items-center gap-2 border-t border-border"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                Edit Match
              </button>
            )}
            {(isAdmin || isScorer) && isMatchCompleted(match.status) && (
              <button
                onClick={() => {
                  setActiveMenu(null);
                  setEditMatchTarget(match);
                }}
                className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-raised transition-colors flex items-center gap-2 border-t border-border"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                Edit Stream URL
              </button>
            )}
            {isAdmin && !isMatchCompleted(match.status) && (
              <button onClick={() => { setActiveMenu(null); setConfirmDiscard(match); }} className="w-full px-4 py-2.5 text-left text-sm text-error hover:bg-error/10 transition-colors flex items-center gap-2 border-t border-border">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3,6 5,6 21,6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                Discard Match
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderMatchSummaryRow = (match: MatchWithTeams) => {
    const statusInfo = getStatusLabel(match.status);
    const score = getMatchScore(match);
    const canStart = (isAdmin || isScorer) && match.status === 'scheduled';
    const canResume = (isAdmin || isScorer) && isMatchLive(match.status);

    return (
      <tr key={match.id} className="border-b border-border last:border-b-0 hover:bg-surface-raised/50 transition-colors">
        <td className="px-3 py-3 text-text-muted text-sm font-medium whitespace-nowrap">#{match.matchNumber}</td>
        <td className="px-2 py-3">
          <div className="flex items-center gap-1.5">
            {match.teamA?.logoUrl ? (
              <img src={match.teamA.logoUrl} alt={match.teamA.shortName} loading="lazy" className="w-4 h-4 rounded object-contain" onError={e => { e.currentTarget.style.display = 'none'; }} />
            ) : match.teamA ? (
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: match.teamA.color }} />
            ) : null}
            <span className="text-sm font-bold" style={{ color: match.teamA?.color }}>{match.teamA?.shortName ?? '?'}</span>
          </div>
        </td>
        <td className="px-2 py-3">
          <div className="flex items-center gap-1.5">
            {match.teamB?.logoUrl ? (
              <img src={match.teamB.logoUrl} alt={match.teamB.shortName} loading="lazy" className="w-4 h-4 rounded object-contain" onError={e => { e.currentTarget.style.display = 'none'; }} />
            ) : match.teamB ? (
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: match.teamB.color }} />
            ) : null}
            <span className="text-sm font-bold" style={{ color: match.teamB?.color }}>{match.teamB?.shortName ?? '?'}</span>
          </div>
        </td>
        <td className="px-2 py-3 whitespace-nowrap">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${match.matchType === 'league' ? 'bg-serving/20 text-serving' : 'bg-warning/20 text-warning'}`}>
            {match.matchType === 'league' ? 'L' : 'P'}
          </span>
        </td>
        <td className="px-2 py-3 text-xs text-text-secondary whitespace-nowrap">{formatDate(match.date)}</td>
        <td className="px-2 py-3">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${statusInfo.bgClass} ${statusInfo.textClass}`}>
            {statusInfo.label}
          </span>
        </td>
        <td className="px-2 py-3">
          <div className="flex flex-col gap-0.5">
            {score.map((s, i) => (
              <span key={i} className="text-[11px] text-text-secondary font-mono leading-tight">{s}</span>
            ))}
          </div>
        </td>
        <td className="px-2 py-3 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            {isAdmin && !isMatchCompleted(match.status) && (
              <button
                onClick={() => setEditMatchTarget(match)}
                className="text-xs font-bold px-2 py-1 rounded bg-primary/20 text-primary border border-primary/40 hover:bg-primary/30 transition-colors whitespace-nowrap"
                title="Edit Match"
              >
                EDIT
              </button>
            )}
            {(isAdmin || isScorer) && isMatchCompleted(match.status) && (
              <button
                onClick={() => setEditMatchTarget(match)}
                className="text-xs font-bold px-2 py-1 rounded bg-info/20 text-info border border-info/40 hover:bg-info/30 transition-colors whitespace-nowrap"
                title="Edit Stream URL"
              >
                EDIT URL
              </button>
            )}
            {canStart && (
              <button
                onClick={() => setStartMatchTarget(match)}
                className="text-xs font-bold px-2 py-1 rounded bg-success/20 text-success border border-success/40 hover:bg-success/30 transition-colors whitespace-nowrap"
              >
                START
              </button>
            )}
            {canResume && (
              <button
                onClick={() => navigate(`/match/${match.id}/resume`)}
                className="text-xs font-bold px-2 py-1 rounded bg-warning/20 text-warning border border-warning/40 hover:bg-warning/30 transition-colors whitespace-nowrap"
              >
                RESUME
              </button>
            )}
            {isMatchCompleted(match.status) && (
              <button
                onClick={() => navigate(`/match/${match.id}/resume`)}
                className="text-xs font-bold px-2 py-1 rounded bg-surface-raised text-text-secondary border border-border hover:border-text-muted transition-colors whitespace-nowrap"
              >
                VIEW
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveMenu(null);
    if (activeMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeMenu]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-text-secondary">Tournament not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        onSignOut={onSignOut}
        title={tournament.name}
        subtitle={`${new Date(tournament.startDate).toLocaleDateString()} → ${new Date(tournament.endDate).toLocaleDateString()}${tournament.venue ? ` · ${tournament.venue}` : ''}`}
        onNavigate={() => navigate('/tournaments')}
        navIcon="back"
        tournamentId={tournamentId}
      />

      <div className="flex-1 overflow-y-auto safe-area-bottom">
        <div className="max-w-2xl mx-auto">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-2 px-4 pt-4">
            <div className="card text-center">
              <p className="text-2xl font-bold text-text-primary">{stats.total}</p>
              <p className="text-xs text-text-muted">Total</p>
            </div>
            <div className="card text-center">
              <p className="text-2xl font-bold text-success">{stats.completed}</p>
              <p className="text-xs text-text-muted">Done</p>
            </div>
            <div className="card text-center">
              <p className="text-2xl font-bold text-warning">{stats.live}</p>
              <p className="text-xs text-text-muted">Live</p>
            </div>
            <div className="card text-center">
              <p className="text-2xl font-bold text-text-muted">{stats.pending}</p>
              <p className="text-xs text-text-muted">Pending</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid gap-2 px-4 pt-3" style={{ gridTemplateColumns: isAdmin ? '1fr 1fr' : '1fr' }}>
            {isAdmin && (
              <button
                onClick={() => setShowScheduleModal(true)}
                className="btn-primary bg-success text-white font-bold"
              >
                + SCHEDULE MATCH
              </button>
            )}
            <button
              onClick={() => setShowPointsTable(true)}
              className="card flex items-center justify-center gap-2 text-sm font-semibold text-text-primary hover:border-warning/50 hover:text-warning transition-colors active:scale-[0.99]"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
              </svg>
              Statistics
            </button>
          </div>

          {/* Participating Teams */}
          {participatingTeams.length > 0 && (
            <div className="card bg-surface-raised mx-4 mt-3">
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                Participating Teams ({participatingTeams.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {participatingTeams.map(team => (
                  <TeamChip key={team.id} team={team} size="sm" />
                ))}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-border mx-4 mt-4">
            {(['matches', 'summary'] as ActiveTab[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2.5 text-sm font-semibold capitalize transition-colors ${
                  activeTab === tab
                    ? 'text-serving border-b-2 border-serving'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
              >
                {tab === 'matches' ? 'Match Cards' : 'Match Summary'}
              </button>
            ))}
          </div>

          {/* Tab: Match Cards */}
          {activeTab === 'matches' && (
            <div className="px-4 py-4 space-y-4">
              {leagueMatches.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
                    League Matches ({leagueMatches.length})
                  </h3>
                  <div className="space-y-2">{leagueMatches.map(renderMatchCard)}</div>
                </div>
              )}
              {playoffMatches.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
                    Playoff Matches ({playoffMatches.length})
                  </h3>
                  <div className="space-y-2">{playoffMatches.map(renderMatchCard)}</div>
                </div>
              )}
              {matches.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-text-secondary text-sm">No matches scheduled yet</p>
                  {isAdmin && (
                    <button onClick={() => setShowScheduleModal(true)} className="btn-primary bg-success text-white mt-4">
                      SCHEDULE FIRST MATCH
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tab: Match Summary Table */}
          {activeTab === 'summary' && (
            <div className="py-4">
              {matches.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <p className="text-text-secondary text-sm">No matches scheduled yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[640px]">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-3 py-2 text-[10px] font-bold text-text-muted uppercase tracking-wider">#</th>
                        <th className="px-2 py-2 text-[10px] font-bold text-text-muted uppercase tracking-wider">Team A</th>
                        <th className="px-2 py-2 text-[10px] font-bold text-text-muted uppercase tracking-wider">Team B</th>
                        <th className="px-2 py-2 text-[10px] font-bold text-text-muted uppercase tracking-wider">Type</th>
                        <th className="px-2 py-2 text-[10px] font-bold text-text-muted uppercase tracking-wider">Date</th>
                        <th className="px-2 py-2 text-[10px] font-bold text-text-muted uppercase tracking-wider">Status</th>
                        <th className="px-2 py-2 text-[10px] font-bold text-text-muted uppercase tracking-wider">Score</th>
                        <th className="px-2 py-2 text-[10px] font-bold text-text-muted uppercase tracking-wider whitespace-nowrap">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matches.map(renderMatchSummaryRow)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Discard Confirmation */}
      {confirmDiscard && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl p-6 max-w-sm w-full">
            <h2 className="text-lg font-bold text-text-primary mb-2">Discard Match?</h2>
            <p className="text-text-secondary text-sm mb-4">
              Are you sure you want to discard Match #{confirmDiscard.matchNumber}? This will delete all match data. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDiscard(null)} disabled={discarding} className="flex-1 btn-secondary">Cancel</button>
              <button onClick={handleDiscardMatch} disabled={discarding} className="flex-1 bg-error text-white py-3 rounded-lg font-semibold disabled:opacity-50">
                {discarding ? 'Discarding...' : 'Discard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPointsTable && tournamentId && (
        <TournamentStatsModal
          tournamentId={tournamentId}
          tournamentName={tournament?.name}
          onClose={() => setShowPointsTable(false)}
        />
      )}

      {showScheduleModal && tournament && (
        <ScheduleMatchModal
          tournament={tournament}
          participatingTeams={participatingTeams}
          onClose={() => setShowScheduleModal(false)}
          onScheduled={() => {
            setShowScheduleModal(false);
            refreshData();
          }}
        />
      )}

      {editMatchTarget && tournament && (
        <ScheduleMatchModal
          tournament={tournament}
          participatingTeams={participatingTeams}
          editMatch={{
            id: editMatchTarget.id,
            matchNumber: editMatchTarget.matchNumber,
            matchType: editMatchTarget.matchType,
            teamAId: editMatchTarget.teamAId,
            teamBId: editMatchTarget.teamBId,
            date: editMatchTarget.date,
            matchTime: editMatchTarget.matchTime,
            liveStreamUrl: editMatchTarget.liveStreamUrl,
          }}
          isCompleted={editMatchTarget.status === 'match-won'}
          onClose={() => setEditMatchTarget(null)}
          onScheduled={() => {
            setEditMatchTarget(null);
            refreshData();
          }}
        />
      )}

      {startMatchTarget && startMatchTarget.teamA && startMatchTarget.teamB && (
        <StartMatchTossModal
          matchId={startMatchTarget.id}
          matchNumber={startMatchTarget.matchNumber}
          teamA={startMatchTarget.teamA}
          teamB={startMatchTarget.teamB}
          onClose={() => setStartMatchTarget(null)}
          onStarted={handleMatchStarted}
        />
      )}
    </div>
  );
}
